import { motion } from "framer-motion";
import { Banknote, Bot, ClipboardCheck, FileText, ReceiptText, ShieldAlert } from "lucide-react";

const agents = [
  { n: "01", icon: FileText, title: "Scribe Agent & Coding Agent", body: "Drafts structured notes, suggests CPT and ICD-10 context, and moves the encounter toward a clean chart-to-charge handoff." },
  { n: "02", icon: ClipboardCheck, title: "Eligibility and Authorization Agent", body: "Checks coverage, detects authorization needs, queues missing payer requirements, and keeps front-office work visible before the visit." },
  { n: "03", icon: ReceiptText, title: "Billing Agent", body: "Turns charges into claim-ready work queues with payer-aware checks, coding support, and submission tracking." },
  { n: "04", icon: Banknote, title: "Payments Agent", body: "Reads ERA/835 files, auto-posts payments, reconciles exceptions, and keeps the revenue picture current." },
  { n: "05", icon: ShieldAlert, title: "Denial Agent", body: "Finds denial risk before submission and routes payer rejections with the evidence needed for fast correction." },
  { n: "06", icon: Bot, title: "AR Follow up Agent", body: "Prioritizes aging claims, prepares follow-up actions, and escalates only the accounts that need human judgment." },
];

export const Manifesto = () => {
  return (
    <section id="agents" data-testid="agents" className="relative z-10 bg-[#F2F8FC] py-16 md:py-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2880B8] mb-5">/ Astra AI Agents</p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-[#0C2A40] leading-[1.02]"
          >
            Six autonomous agents that <span className="font-extrabold text-[#2880B8]">never clock out.</span>
          </motion.h2>
          <p className="mt-6 text-[#51667A] text-lg leading-relaxed">
            Astra does not just store your data - it acts on it. Purpose-built agents run clinical documentation, front-office checks, billing and revenue follow-up around the clock.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {agents.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 3) * 0.1, duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                className="group bg-white rounded-2xl border border-[#D8E6F1] p-8 hover:border-[#2880B8] hover:shadow-[0_24px_50px_-24px_rgba(40,128,184,0.4)] transition-all duration-500"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-xl bg-[#E7F1F8] flex items-center justify-center group-hover:bg-[#2880B8] transition-colors duration-500">
                    <Icon size={22} className="text-[#2880B8] group-hover:text-white transition-colors duration-500" strokeWidth={1.6} />
                  </div>
                  <span className="font-mono text-3xl font-bold text-[#E7F1F8] group-hover:text-[#2880B8] transition-colors duration-500">{a.n}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#0C2A40]">{a.title}</h3>
                <p className="mt-3 text-[#51667A] leading-relaxed">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
